import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, Tooltip, TooltipProps } from 'recharts';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import stationManagement from '../../../utils/api/station-management';

interface ChartDataProperties {
  name: string;
  value: number;
  color: string;
}

/**
 * Colors for chart
 * @param status
 * @returns
 */
function getStatusColor(status: string) {
  switch (status) {
    case 'Available':
      return '#2AA700';
    case 'Charging':
      return '#4ABDFB';
    case 'Unavailable':
      return '#6C757D';
    case 'Faulted':
      return '#FFD000';
    default:
      return '#CCCCCC';
  }
}

/**
 * Station status distribution chart component
 * @returns {any} The StationStatusDistributionChart component
 */
function StationStatusDistributionChart() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const { selectedStationPerformanceList } = useAppSelector((state) => state.stationManagement);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [loading, setLoading] = useState(false);

  /**
   * FUnction for fetching power consumed report
   */
  const fetchPowerConsumedReport = async () => {
    setLoading(true);
    try {
      const response = await stationManagement.fetchConnectorStatus({
        chargingStation: selectedStationPerformanceList?.id,
      });

      if (response.status === 200) {
        const statusCounts = response.data.statusCounts || [];

        const chartData = statusCounts
          .filter((item: { status: string }) =>
            ['Unavailable', 'Available', 'Faulted', 'Charging'].includes(item.status),
          )
          .map((item: { status: string; count: number }) => ({
            name: item.status,
            value: item.count,
            color: getStatusColor(item.status),
          }));

        setBarGraphData(chartData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function for tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="connector-status-custom-tooltip">
          <p className="label" style={{ color: data?.payload?.color }}>
            {`${data.name} : ${data.value}`}
          </p>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    fetchPowerConsumedReport();
  }, [selectedStationPerformanceList]);
  return (
    <div className="station-status-distributionChart-wrap">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">{t('stationStatusDistribution')}</div>
        </div>
        {loading ? (
          <center className="loader">
            <BatteryLoader />
          </center>
        ) : (
          <div className="energy-report-container">
            <div className="piechart-container">
              <PieChart width={250} height={250} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <Pie
                  data={barGraphData}
                  cx={125}
                  cy={125}
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  isAnimationActive={false}>
                  {barGraphData.map((entry) => (
                    <Cell key={entry.name} fill={String(entry.color)} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 1000 }} />
              </PieChart>

              <div className="total-sessions-wrap">
                <p>{t('devicesCount')}</p>
                <span>{barGraphData.reduce((sum, d) => sum + d.value, 0)} </span>
              </div>
            </div>

            <div className="PieChart-heading">
              <div className="pie-chart-label">
                {['Available', 'Charging', 'Unavailable', 'Faulted'].map((status) => {
                  const color = getStatusColor(status);
                  const matchingItem = barGraphData.find((d) => d.name === status);
                  const value = matchingItem ? matchingItem.value : 0;
                  return (
                    <div key={status} className="each-label">
                      <div
                        className="each-label-color"
                        style={{ '--label-color': color } as React.CSSProperties}
                      />
                      <p>{t(status.toLowerCase())}</p>
                      <div className="each-label-value">
                        <span>{value}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StationStatusDistributionChart;
